import React, { FC, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { usePlaidLink, PlaidLinkOnSuccessMetadata, PlaidLinkError } from 'react-plaid-link';
import { ApolloClient, ApolloError, NormalizedCacheObject, useApolloClient, useMutation } from '@apollo/client';
import * as PaymentMutation from 'graphql/payment.graphql';
import { createSetupIntent } from 'api/data/payment';
import { apolloErrorFormatter } from 'utils';
import useFinixAuth from 'hooks/useFinixAuth';
import PaymentForm from '../PaymentForm';
import { BankAccount, ProcessPaymentData, PaymentMethodType } from 'api/data/payment/types';
import { ProviderProps } from '../types';
import { Response, User } from 'api/data/response/types';

interface PlaidProps extends Omit<ProviderProps, 'onResponseChanged' | 'onSubmit'> {
  onResponseChanged?: (response: Response) => void;
  onSubmit?: (paymentData: ProcessPaymentData) => Promise<void>;
  response: Response;
  provider?: string;
}

interface CreateLinkTokenData {
  createLinkToken: {
    linkToken: string;
    expiration: string;
  };
}

interface ExchangePublicTokenForProcessorTokenData {
  exchangePublicTokenForProcessorToken: {
    processorToken: string;
  };
}

/**
 * Flow summary:
 * 1. Payer fills the form and clicks "Connect your bank account" to start bank account connection
 * 2. Create a link token
 * 3. Open Plaid Link Modal and choose account
 * 4. Exchange public token + account_id for processor token
 * 5. Create setup intent (with the processor token returned)
 * 6. Payer clicks on "Pay $x" and payment data is submitted
 */
const Plaid: FC<PlaidProps> = ({
  onSubmit,
  onResponseChanged,
  buttonText,
  paymentError,
  setLoading,
  loading = false,
  disableInputEmail = false,
  response,
  provider,
}) => {
  const { sessionKey } = useFinixAuth(provider);
  const [linkToken, setLinkToken] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [bankAccount, setBankAccount] = useState<BankAccount | undefined>();
  const [errorMessage, setErrorMessage] = useState('');
  const [shouldOpenPlaid, setShouldOpenPlaid] = useState(false);
  const [shouldStartBankConnection, setShouldStartBankConnection] = useState(false);
  // note: since response.user can take some time to update, we are
  // using this state to make sure the last info typed is being sent to createSetupIntent
  const [user, setUser] = useState<User>({ email: '', zipcode: '', fullName: '' });

  const [createLinkTokenMutation] = useMutation<CreateLinkTokenData>(PaymentMutation.CreateLinkToken);
  const [exchangePublicTokenMutation] = useMutation<ExchangePublicTokenForProcessorTokenData>(
    PaymentMutation.ExchangePublicTokenForProcessorToken,
  );
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;

  const handleError = useCallback(
    error => {
      const errorMessage = apolloErrorFormatter(error as ApolloError);
      setLoading(false);
      setErrorMessage(errorMessage);
    },
    [setLoading],
  );

  const handleConnectYourBankAccount = (event: React.SyntheticEvent, user: User) => {
    event.preventDefault();
    setErrorMessage('');
    setLoading(true);

    setUser(user);
    setShouldStartBankConnection(true);
  };

  const createLinkToken = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);

      const result = await createLinkTokenMutation({
        variables: { userId: response.user?.id },
      });

      const linkToken = result.data?.createLinkToken?.linkToken;

      if (!linkToken) throw new Error('Failed to get link token');

      setLinkToken(linkToken);
      setShouldOpenPlaid(true);
    } catch (error) {
      handleError(error);
    }
  }, [createLinkTokenMutation, response?.user?.id, setLoading, handleError]);

  const connectBankAccount = async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    const accountId = metadata.accounts[0]?.id;
    if (!accountId) {
      return handleError(new Error('No account selected'));
    }

    if (!response.id || !response.user?.id || !response.user?.email) {
      handleError(new Error('Missing required user data'));
    }

    setLoading(true);
    setSelectedAccountId(accountId);

    const newBankAccount = {
      accountType: metadata.accounts[0].subtype || '',
      bankName: metadata.institution?.name || '',
      last4: metadata.accounts[0].mask || '',
    };

    try {
      const { data: exchangePublicTokenData } = await exchangePublicTokenMutation({
        variables: {
          input: {
            publicToken,
            userId: response.user?.id,
            accountId,
            processor: 'finix',
            accountType: newBankAccount.accountType,
            bankName: newBankAccount.bankName,
            accountLast4: newBankAccount.last4,
          },
        },
      });

      const { paymentMethodId } = await createSetupIntent(
        {
          responseId: response.id ?? '',
          email: user?.email,
          token: exchangePublicTokenData?.exchangePublicTokenForProcessorToken.processorToken,
          type: 'PLAID',
        },
        client,
      );

      setPaymentMethodId(paymentMethodId);
      setBankAccount(newBankAccount);

      setLoading(false);
    } catch (error) {
      handleError(error);
    }
  };

  const { open: openPlaidLink, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) =>
      void connectBankAccount(publicToken, metadata),
    onExit: (error: PlaidLinkError | null) => {
      if (error) {
        handleError(new Error(error.error_message));
      }
      setLoading(false);
      setLinkToken('');
      setShouldOpenPlaid(false);
    },
  });

  useEffect(() => {
    const startBankConnection = async () => {
      if (shouldStartBankConnection && !bankAccount?.last4 && response?.user?.id) {
        setShouldStartBankConnection(false);
        await createLinkToken();
        return;
      }
    };
    void startBankConnection();
  }, [bankAccount?.last4, createLinkToken, response?.user?.id, shouldStartBankConnection]);

  useEffect(() => {
    const isReadyToOpenPlaid = linkToken && ready && shouldOpenPlaid;
    if (isReadyToOpenPlaid) {
      (openPlaidLink as () => void)();
      setShouldOpenPlaid(false);
    }
  }, [linkToken, openPlaidLink, ready, shouldOpenPlaid]);

  const handlePaySubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      if (!paymentMethodId || !sessionKey || !selectedAccountId) {
        handleError(new Error('Missing required payment information'));
      }

      if (onSubmit) {
        const paymentData: ProcessPaymentData = {
          paymentMethodId,
          paymentMethodType: PaymentMethodType.ACH,
          financialConnectionsAccountId: selectedAccountId,
          response,
          sessionKey,
        };
        await onSubmit(paymentData);
      }
      setLoading(false);
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <PaymentForm
      buttonText={buttonText}
      loading={loading}
      disableInputEmail={disableInputEmail}
      errorMessage={errorMessage}
      paymentError={typeof paymentError === 'string' ? paymentError : undefined}
      response={response}
      onResponseChanged={onResponseChanged}
      bankAccount={bankAccount}
      handleSubmit={async (event, user) =>
        bankAccount?.last4 ? handlePaySubmit(event) : handleConnectYourBankAccount(event, user)
      }
      connectBankAccount={createLinkToken}
      setErrorMessage={setErrorMessage}
      setLoading={setLoading}
    />
  );
};

export default Plaid;
